import { z } from 'zod';

const storageKey = 'budgy_waitlist_utm';

const waitlistUtmSchema = z.object({
  source: z.string().optional(),
  medium: z.string().optional(),
  campaign: z.string().optional(),
  term: z.string().optional(),
  content: z.string().optional(),
});

type WaitlistUtm = z.infer<typeof waitlistUtmSchema>;

const hasAnyValue = (utm: WaitlistUtm) =>
  Object.values(utm).some((value) => value !== undefined && value !== '');

/**
 * Reads UTM params from the raw query string. The router's parsed search
 * JSON-decodes values, which turns numeric ad IDs into numbers and loses
 * precision on long ones, so the raw string is the only faithful source.
 */
export const parseWaitlistUtm = (searchStr: string): WaitlistUtm => {
  const params = new URLSearchParams(searchStr);
  const read = (key: string) => params.get(key) ?? undefined;

  return {
    source: read('utm_source'),
    medium: read('utm_medium'),
    campaign: read('utm_campaign'),
    term: read('utm_term'),
    content: read('utm_content'),
  };
};

/**
 * Remembers the landing URL's UTM params for the rest of the browser session,
 * so a visitor who browses the blog before signing up keeps their attribution.
 * A later landing with new UTM params replaces the stored set.
 */
export const storeWaitlistUtm = (utm: WaitlistUtm): void => {
  if (typeof window === 'undefined' || !hasAnyValue(utm)) {
    return;
  }

  try {
    window.sessionStorage.setItem(storageKey, JSON.stringify(utm));
  } catch {
    // sessionStorage may be unavailable (private mode, quota exceeded, etc.)
  }
};

export const readWaitlistUtm = (): WaitlistUtm | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  try {
    const raw = window.sessionStorage.getItem(storageKey);
    if (raw == null) {
      return undefined;
    }

    const parsed = waitlistUtmSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : undefined;
  } catch {
    return undefined;
  }
};
